import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import { ListGroupItem, ListGroup, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { DocumentSettings, ReportsSettingsData } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { serviceName } from '../../../shared/appointmint/appointment';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DocumentConfigurationModal from './DocumentConfigurationModal';
import { Guid } from '../../../shared/utils';
import { getIconComponent } from '../../../shared/documentIconPicker';
import AppButton from '../../../components/AppButton';

export interface DocumentsSettingsProps extends SettingsItemContentProps {
    values: ReportsSettingsData,
    onChange: (settings: ReportsSettingsData, save?: boolean) => void,
}

const generateEmptyDocumentSettings = (): DocumentSettings => ({
    id: Guid.newGuid(),
    availableForAppointmentTypes: [],
    availableForAppointmentsManager: false,
    name: '',
});

const DocumentsSettings : FC<DocumentsSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const documents = values.documents ?? []

    const [editingDocument, setEditingDocument] = useState<DocumentSettings|undefined>(undefined);
    const [deletingId, setDeletingId] = React.useState<string|undefined>(undefined);
    
    const closeDeleteModal = React.useCallback(() => setDeletingId(undefined), [deletingId]);
    const onAddNew = useCallback(() => setEditingDocument(generateEmptyDocumentSettings()), [setEditingDocument]);
    const onSelect = useCallback((document: DocumentSettings) => () => setEditingDocument(document), [setEditingDocument]);
    const onCloseDocument = useCallback(() => setEditingDocument(undefined), [setEditingDocument]);
    

    const onSaveDocument = useCallback((newDocument?: DocumentSettings) => {
        onChange({
            ...values,
            documents: !newDocument 
                ? documents.filter(r => r.id !== deletingId) 
                : documents.map(d => d.id === newDocument.id ? newDocument : d)                    
                    .concat(!documents.some(d => d.id === newDocument.id) ? [newDocument] : []),
        }, true);
        setDeletingId(undefined);
        setEditingDocument(undefined);
    }, [editingDocument, documents, deletingId, values, onChange]);

    return (
        <>
            <Alert color="secondary" isOpen>
                {t(TK.documentsSettingsDescription)}
            </Alert>

            <ListGroup>
                { documents.map((document) => {
                    const DocIcon = getIconComponent(document.icon) || InsertDriveFileIcon;
                    return (
                    <ListGroupItem key={document.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacer-XS)' }}>
                        <DocIcon style={{ color: document.colorCode, flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacer-XXS)', minWidth: 0 }}>
                            <AppButton id={`${id}-open-${document.id}-button`} onClick={onSelect(document)} color="link" style={{ padding: 0, textAlign: 'left', justifyContent: 'flex-start' }}>
                                {document.name}
                            </AppButton>
                            {document.availableForAppointmentTypes.length > 0 && (
                                <div style={{ display: 'flex', gap: 'var(--spacer-XS)', flexWrap: 'wrap' }}>
                                    {document.availableForAppointmentTypes.map(type => <Badge key={type}>{serviceName(type, generalSettings)}</Badge>)}
                                </div>
                            )}
                        </div>
                    </ListGroupItem>
                )})}
                <ListGroupItem>
                    <IconButton id={`${id}-add-button`} onClick={onAddNew} size="small"><AddCircleIcon/>&nbsp;{t(TK.new)}</IconButton>
                </ListGroupItem>
            </ListGroup>

            <DocumentConfigurationModal 
                defaultValue={editingDocument}
                onSave={onSaveDocument}
                onClose={onCloseDocument}
                onDelete={setDeletingId}
            />
            
            <Modal isOpen={!!deletingId} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`${id}-confirm-delete-button`} color="danger" onClick={() => onSaveDocument()}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default DocumentsSettings;